import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import EmptyState from "@/components/EmptyState";
import { CalendarBlank, TrendUp, TrendDown, ArrowRight } from "@phosphor-icons/react";

const fmt = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(n || 0));
const fmtSigned = (n) => {
    const v = Number(n || 0);
    const abs = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Math.abs(v));
    return v >= 0 ? `${abs} Dr` : `${abs} Cr`;
};

export default function CustomerMonthlySummary() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!user?.party_id) return;
        setLoading(true);
        api.get(`/ledger/${user.party_id}/monthly-summary`)
            .then((r) => setSummary(r.data))
            .catch((e) => { toast.error(formatApiError(e)); setSummary(null); })
            .finally(() => setLoading(false));
    }, [user]);

    const totals = useMemo(() => {
        if (!summary?.months) return { received: 0, paid: 0, other: 0 };
        return summary.months.reduce((acc, m) => ({
            received: acc.received + Number(m.received || 0),
            paid: acc.paid + Number(m.paid || 0),
            other: acc.other + Number(m.other || 0),
        }), { received: 0, paid: 0, other: 0 });
    }, [summary]);

    // Customer drills into their own ledger — filters by month range via query params
    const openMonth = (row) => {
        navigate(`/portal/ledger?date_from=${row.date_from}&date_to=${row.date_to}`);
    };

    return (
        <div className="space-y-6" data-testid="customer-monthly-summary-page">
            <div>
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500">{summary?.party?.name || ""}</div>
                <h1 className="mt-1 font-display text-4xl font-bold tracking-tight text-slate-900">Monthly Summary</h1>
                <p className="mt-2 text-sm text-slate-500">
                    Your received, paid and closing balance grouped by month. Tap any month to open that month's full ledger.
                </p>
            </div>

            {loading || !summary ? (
                <EmptyState icon={CalendarBlank} title={loading ? "Loading…" : "No data"} description={loading ? "Fetching your monthly summary." : "We couldn't load your summary. Please refresh."} />
            ) : summary.months.length === 0 ? (
                <EmptyState icon={CalendarBlank} title="No transactions yet" description="Once slips are verified, your monthly totals will appear here." />
            ) : (
                <>
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                        <StatCard label="Opening" value={fmtSigned(summary.opening_balance)} tone="slate" />
                        <StatCard label="Total received" value={fmt(totals.received)} tone="emerald" icon={TrendUp} />
                        <StatCard label="Total paid" value={fmt(totals.paid)} tone="violet" icon={TrendDown} />
                        <StatCard label="Closing" value={fmtSigned(summary.closing_balance)} tone={summary.closing_balance >= 0 ? "amber" : "blue"} accent />
                    </div>

                    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm" data-testid="cust-ms-table">
                                <thead>
                                    <tr className="bg-emerald-700 text-left text-[10px] font-semibold uppercase tracking-wider text-white">
                                        <th className="px-4 py-3 border-r border-emerald-600">Month</th>
                                        <th className="px-4 py-3 text-right border-r border-emerald-600">Received</th>
                                        <th className="px-4 py-3 text-right border-r border-emerald-600">Paid</th>
                                        <th className="px-4 py-3 text-right border-r border-emerald-600">Other</th>
                                        <th className="px-4 py-3 text-right border-r border-emerald-600">Closing Balance</th>
                                        <th className="px-4 py-3 text-center w-12" aria-label="Open"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {summary.months.map((m) => (
                                        <tr
                                            key={m.month}
                                            data-testid={`cust-ms-row-${m.month}`}
                                            onClick={() => openMonth(m)}
                                            className="cursor-pointer border-t border-slate-100 transition-colors hover:bg-blue-50/60"
                                            title={`Open ledger for ${m.month_label}`}
                                        >
                                            <td className="px-4 py-3 font-semibold text-slate-900">{m.month_label}</td>
                                            <td className="px-4 py-3 text-right tabular-nums text-emerald-700 font-medium">
                                                {m.received > 0 ? fmt(m.received) : <span className="text-slate-300">—</span>}
                                            </td>
                                            <td className="px-4 py-3 text-right tabular-nums text-violet-700 font-medium">
                                                {m.paid > 0 ? fmt(m.paid) : <span className="text-slate-300">—</span>}
                                            </td>
                                            <td className="px-4 py-3 text-right tabular-nums text-slate-600">
                                                {m.other !== 0 ? fmtSigned(m.other) : <span className="text-slate-300">—</span>}
                                            </td>
                                            <td className="px-4 py-3 text-right tabular-nums font-semibold text-slate-900">
                                                {fmtSigned(m.closing_balance)}
                                            </td>
                                            <td className="px-4 py-3 text-center text-blue-600">
                                                <ArrowRight size={14} weight="bold" />
                                            </td>
                                        </tr>
                                    ))}
                                    <tr data-testid="cust-ms-total-row" className="border-t-2 border-emerald-700 bg-slate-50 font-semibold">
                                        <td className="px-4 py-3 text-slate-700 uppercase tracking-wider text-[10px]">Totals · {summary.months.length} months</td>
                                        <td className="px-4 py-3 text-right tabular-nums text-emerald-700" data-testid="cust-ms-total-received">{fmt(totals.received)}</td>
                                        <td className="px-4 py-3 text-right tabular-nums text-violet-700" data-testid="cust-ms-total-paid">{fmt(totals.paid)}</td>
                                        <td className="px-4 py-3 text-right tabular-nums text-slate-700" data-testid="cust-ms-total-other">{fmtSigned(totals.other)}</td>
                                        <td className="px-4 py-3 text-right tabular-nums text-slate-900" data-testid="cust-ms-closing">{fmtSigned(summary.closing_balance)}</td>
                                        <td />
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <p className="text-center text-xs italic text-slate-500">
                        Tap any month row to open your ledger filtered to that month.
                    </p>
                </>
            )}
        </div>
    );
}

function StatCard({ label, value, tone = "slate", icon: Icon, accent }) {
    const tones = {
        slate: "text-slate-900",
        emerald: "text-emerald-700",
        violet: "text-violet-700",
        amber: "text-amber-700",
        blue: "text-blue-700",
    };
    return (
        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-start justify-between">
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500">{label}</div>
                {Icon && <Icon size={16} className="text-slate-400" />}
            </div>
            <div className={`mt-2 font-display text-xl font-bold tabular-nums ${tones[tone] || tones.slate} ${accent ? "text-2xl" : ""}`}>{value}</div>
        </div>
    );
}
